import React, { useState, useEffect, useRef } from 'react';
import { CreditCard, Lock, AlertCircle } from 'lucide-react';

export default function PayUPayment({ bookingId, bookingData }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payuData, setPayuData] = useState(null);
  const formRef = useRef(null);
  const API_URL = process.env.REACT_APP_API_URL || 'https://api.boomontrip.com';

  useEffect(() => {
    if (bookingId) {
      initiatePayment();
    }
  }, [bookingId]);

  const initiatePayment = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('💳 Initiating PayU payment for booking:', bookingId);

      const response = await fetch(`${API_URL}/api/payments/initiate-payu`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bookingId: bookingId
        })
      });

      const data = await response.json();
      console.log('📦 PayU Response:', data);

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to initiate payment');
      }

      if (!data.data || !data.data.payuData) {
        throw new Error('Invalid payment data received');
      }

      console.log('✅ PayU data received:', data.data.payuData);

      console.log(' Hash received:', data.data.payuData.hash?.substring(0, 40) + '...');
      console.log(' Transaction ID:', data.data.payuData.txnid);
      console.log(' Amount:', data.data.payuData.amount);
      console.log(' Payment URL:', data.data.payuData.paymentUrl);

      setPayuData(data.data.payuData);

    } catch (err) {
      console.error(' Error initiating payment:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = () => {
    try {
      console.log(' Starting payment submission...');

      if (!payuData) {
        throw new Error('Payment data not loaded. Please refresh the page and try again.');
      }

      if (!payuData.hash || !payuData.txnid || !payuData.amount) {
        throw new Error('Missing required payment fields. Please try again or contact support.');
      }

      if (!formRef.current) {
        throw new Error('Payment form not ready. Please refresh the page.');
      }

      console.log(' Submitting PayU form with data:');
      console.log('  Key:', payuData.key);
      console.log('  Transaction ID:', payuData.txnid);
      console.log('  Amount:', payuData.amount);
      console.log('  Email:', payuData.email);
      console.log('  Name:', payuData.firstname);
      console.log('  Phone:', payuData.phone);
      console.log('  Payment URL:', payuData.paymentUrl);
      console.log('  Hash Length:', payuData.hash?.length);
      console.log('  UDF1:', payuData.udf1);
      console.log('  UDF2:', payuData.udf2);
      console.log('  UDF3:', payuData.udf3);
      console.log('  UDF4:', payuData.udf4);
      console.log('  UDF5:', payuData.udf5);

      sessionStorage.setItem('payuTransactionId', payuData.txnid);
      sessionStorage.setItem('payuAmount', payuData.amount);

      formRef.current.submit();

      console.log(' Form submitted successfully to PayU');

    } catch (err) {
      console.error(' Payment submission error:', err);
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-semibold">Initializing secure payment...</p>
          <p className="text-sm text-gray-500 mt-2">Please wait while we prepare your payment</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
        <div className="flex items-start">
          <AlertCircle className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-1" />
          <div className="flex-1">
            <h4 className="text-red-800 font-semibold mb-2">Payment Initialization Failed</h4>
            <p className="text-red-700 text-sm mb-4">{error}</p>
            <button
              onClick={initiatePayment}
              className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm font-semibold"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!payuData) {
    return (
      <div className="bg-gray-50 rounded-2xl p-8 text-center">
        <p className="text-gray-600">Preparing payment gateway...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Payment Info Card */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6">
        <div className="flex items-center justify-center mb-4">
          <CreditCard className="w-6 h-6 text-blue-600 mr-2" />
          <h3 className="text-lg font-bold text-gray-800">Secure Card Payment</h3>
        </div>

        <div className="bg-white rounded-xl p-6 mb-6">
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Payment Gateway:</span>
              <span className="font-semibold text-blue-600">PayU (Secure)</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Transaction ID:</span>
              <span className="font-mono text-xs">{payuData.txnid}</span>
            </div>
            {bookingData?.discount && bookingData?.originalPrice && (
              <>
                <div className="flex justify-between pt-2 border-t">
                  <span className="text-gray-600">Original Amount:</span>
                  <span className="text-gray-400 line-through">₹{bookingData.originalPrice.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-600 font-semibold">Discount ({bookingData.discount.percent}%):</span>
                  <span className="text-green-600 font-semibold">-₹{Math.round(bookingData.discount.amount).toLocaleString('en-IN')}</span>
                </div>
              </>
            )}
            <div className="flex justify-between pt-2 border-t">
              <span className="text-gray-700 font-semibold">Amount to Pay:</span>
              <span className="text-2xl font-bold text-orange-600">₹{parseFloat(payuData.amount).toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>

        

        {/* ✅ FIXED: Hidden PayU Form with all required fields */}
        <form
          ref={formRef}
          action={payuData.paymentUrl}
          method="POST"
          style={{ display: 'none' }}
        >
          <input type="hidden" name="key" value={payuData.key} />
          <input type="hidden" name="txnid" value={payuData.txnid} />
          <input type="hidden" name="amount" value={payuData.amount} />
          <input type="hidden" name="productinfo" value={payuData.productinfo} />
          <input type="hidden" name="firstname" value={payuData.firstname} />
          <input type="hidden" name="email" value={payuData.email} />
          <input type="hidden" name="phone" value={payuData.phone} />
          <input type="hidden" name="surl" value={payuData.surl} />
          <input type="hidden" name="furl" value={payuData.furl} />
          <input type="hidden" name="hash" value={payuData.hash} />
          {/* ✅ CRITICAL: UDF fields must be included even if empty */}
          <input type="hidden" name="udf1" value={payuData.udf1 || ''} />
          <input type="hidden" name="udf2" value={payuData.udf2 || ''} />
          <input type="hidden" name="udf3" value={payuData.udf3 || ''} />
          <input type="hidden" name="udf4" value={payuData.udf4 || ''} />
          <input type="hidden" name="udf5" value={payuData.udf5 || ''} />
        </form>

        {/* Pay Now Button */}
        <button
          onClick={handlePayNow}
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 rounded-xl font-bold text-lg hover:shadow-lg transition-all flex items-center justify-center group"
        >
          <Lock className="w-5 h-5 mr-2 group-hover:animate-pulse" />
          Pay ₹{parseFloat(payuData.amount).toLocaleString('en-IN')} Securely
        </button>

        <p className="text-xs text-center text-gray-500 mt-4">
           You will be redirected to PayU's test payment page
        </p>
      </div>

   

      {/* Accepted Cards */}
      <div className="bg-gray-50 rounded-xl p-4">
        <p className="text-xs text-gray-600 text-center mb-3 font-semibold">We Accept</p>
        <div className="flex justify-center items-center space-x-4 flex-wrap gap-2">
          <div className="bg-white rounded px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm">VISA</div>
          <div className="bg-white rounded px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm">Mastercard</div>
          <div className="bg-white rounded px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm">RuPay</div>
          <div className="bg-white rounded px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm">Amex</div>
        </div>
      </div>

      {/* Security Info */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
        <p className="text-xs text-green-800 text-center">
          ✅ 256-bit SSL Encryption | 🛡️ PCI DSS Compliant | 🔒 100% Secure
        </p>
      </div>
    </div>
  );
}